import { useCallback, useContext, useId, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import OfferCapacityWithAgesSelectBox from './OfferCapacityWithAgesSelectBox'
import { OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import OfferCapacityWithAgesVerticalSelection from './OfferCapacityWithAgesVerticalSelection'
import { getMaxGuestCount } from 'lib/offer/occupancyUtils'

import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import { canBookNow, getPackagesForFilters } from 'selectors/offerPage/offerPageSelectors'
import TextButton from 'LuxKit/Button/TextButton'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'

interface Props {
  open: boolean
  capacities?: Array<App.RoomCapacity>
  occupantUnit?: 'guest' | 'traveller'
  childrenAgeLabel?: string
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  disableMultiRoom?: boolean
  canBookNow?: boolean
  relevantPackage: App.OfferPackageOptionView
  rooms?: Array<App.RoomOccupants>
}

const defaultRooms = [generateOfferPageRoom()]

function OfferCapacityWithAgesVerticalSelect(props: Props) {
  const {
    open,
    capacities,
    occupantUnit = 'guest',
    childrenAgeLabel: childrenAgeLabelProp,
    disableMultiRoom,
    onClick,
    canBookNow,
    relevantPackage,
    rooms = defaultRooms,
  } = props
  const intl = useIntl()
  const childrenAgeLabel = childrenAgeLabelProp ?? intl.formatMessage({
    id: 'GBbQFyyy2xTvJgjpJfV6',
    defaultMessage: 'Age at time of check-in',
  })
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const isMobile = useScreenSizeOnly('mobile')
  const [localRooms, setLocalRooms] = useState<Array<App.RoomOccupants | App.Occupants>>(rooms)

  const saveRooms = useCallback(() => {
    dispatch({
      type: OfferPageActions.setRooms,
      rooms: localRooms as any,
    })
  }, [dispatch, localRooms])

  const onClose = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.closeFilter })
    trackEvent('OCCUPANCY_FILTERS_CLOSED')
  }, [dispatch, saveRooms, trackEvent])

  const onContinue = useCallback(() => {
    saveRooms()
    dispatch({ type: OfferPageActions.filterContinue })
  }, [dispatch, saveRooms])

  const maxGuests = useMemo((): number => getMaxGuestCount(capacities), [capacities])

  const onDropdownSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onContinue()
  }, [onContinue])

  const formId = useId()

  const whoIsGoingTitle = intl.formatMessage({
    id: 'aFq3wSJBnNbIUf3fPvdl',
    defaultMessage: 'Who is going?',
  })
  const maxOccupancySubtitle = maxGuests > 0
    ? occupantUnit === 'traveller'
      ? intl.formatMessage(
          {
            id: 'YSRuhixdEx8k6xQuFklH',
            defaultMessage: 'Maximum occupancy is {maxGuests, plural, one {# traveller} other {# travellers}}',
          },
          { maxGuests },
        )
      : intl.formatMessage(
          {
            id: 'x652o2VJ-izWcrkQivOg',
            defaultMessage: 'Maximum occupancy is {maxGuests, plural, one {# guest} other {# guests}}',
          },
          { maxGuests },
        )
    : undefined
  const bookNowLabel = intl.formatMessage({
    id: 'jZ03y3tcdlCZNSnZoPsN',
    defaultMessage: 'Book now',
  })
  const continueLabel = intl.formatMessage({
    id: 'xIHmkInhnbdWHXoN30No',
    defaultMessage: 'Continue',
  })

  return (
    <>
      <OfferCapacityWithAgesSelectBox
        ref={triggerRef}
        occupantUnit={occupantUnit}
        onClick={onClick}
        multiRoomDisabled={disableMultiRoom}
      />
      <DropdownSheet
        size="M"
        open={open && !isMobile}
        onClose={onClose}
        anchorRef={triggerRef}
        triggerRef={triggerRef}
        data-testid="offer-capacity-with-ages-vertical-select-dropdown"
        title={whoIsGoingTitle}
        subtitle={maxOccupancySubtitle}
        footerActions={<>
          {canBookNow && <OfferBookButton
            fit="full-width"
            size="large"
            type="submit"
            form={formId}
            packageView={relevantPackage}
            overrideRooms={localRooms}
          >
            {bookNowLabel}
          </OfferBookButton>}
          {!canBookNow && <TextButton
            kind="primary"
            fit="full-width"
            size="large"
            type="submit"
            form={formId}
          >
            {continueLabel}
          </TextButton>}
        </>}
      >
        <form onSubmit={onDropdownSubmit} id={formId}>
          <OfferCapacityWithAgesVerticalSelection
            capacities={capacities}
            childrenAgeLabel={childrenAgeLabel}
            disableMultiRoom={disableMultiRoom}
            onChange={setLocalRooms}
          />
        </form>
      </DropdownSheet>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  canBookNow: canBookNow(state, appState),
  relevantPackage: getPackagesForFilters(appState, state)[0],
  open: state.activeFilter === 'capacity',
  rooms: state.rooms,
}), OfferCapacityWithAgesVerticalSelect)
